<template>
  <div class="mask">
    <div
      ref="dialogRef"
      class="dialog"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      tabindex="-1"
      @keydown="onKeydown"
    >
      <header class="dialog__header">
        <h2 :id="titleId" class="dialog__title">
          {{ $t("proofreadingPendingWorkDialog.title") }}
        </h2>
      </header>

      <div class="dialog__body">
        <p class="dialog__message">{{ $t("proofreadingPendingWorkDialog.message") }}</p>
      </div>

      <footer class="dialog__footer">
        <button type="button" class="dialog__yes-button" @click="onClickYes">
          {{ $t("proofreadingPendingWorkDialog.yes") }}
        </button>
        <button type="button" class="dialog__no-button" @click="onClickNo">
          {{ $t("proofreadingPendingWorkDialog.no") }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";

/**
 * 校正種別選択画面で作業中データが存在する場合の確認モーダル。
 *
 * 背景クリック・ESC では閉じられず、ボタン押下でのみ閉じる。
 *
 * Vue Modal Dialogs (`vue-modal-dialogs`) で `this.$close(value)` を呼ぶ前提:
 *  - 「続きから再開する」: `this.$close({ action: 'yes' })`
 *  - 「最初から校正する」: `this.$close({ action: 'no' })`
 */
export default Vue.extend({
  data() {
    return {
      titleId: "proofreading-pending-work-dialog-title",
    };
  },
  mounted() {
    this.$nextTick(() => {
      (this.$refs.dialogRef as HTMLElement | undefined)?.focus();
    });
  },
  methods: {
    onClickYes() {
      this.$close({ action: "yes" });
    },
    onClickNo() {
      this.$close({ action: "no" });
    },
    onKeydown(e: KeyboardEvent) {
      // ESC による閉じ操作・親要素への伝播を抑止する
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    },
  },
});

export type ProofreadingPendingWorkDialogResult = {
  action: "yes" | "no";
};
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.mask {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9000;
  background-color: var(--color-overlay);
}

.dialog {
  display: flex;
  flex-direction: column;
  width: min(400px, 92vw);
  margin: 0;
  border: none;
  background-color: var(--color-background-normal);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);
  overflow: hidden;

  &:focus {
    outline: none;
  }
}

.dialog__header {
  padding: 20px 24px 0;
}

.dialog__title {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  text-align: center;
}

.dialog__body {
  padding: 16px 24px 8px;
}

.dialog__message {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  text-align: left;
  color: var(--color-text-normal);
}

.dialog__footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 24px 24px;
}

.dialog__no-button,
.dialog__yes-button {
  position: relative;
  width: 100%;
  padding: 12px 24px;
  border: none;
  border-radius: var(--radius-rounded);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;
}

.dialog__yes-button {
  background-color: var(--color-primary);
  color: var(--color-text-invert);
}

.dialog__no-button {
  background-color: var(--color-surface-muted);
  color: var(--color-text-normal);
}
</style>
